import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store/storeHooks';
import { closeSideBarFunc, openSideBarFunc } from '@/store/reducers/SidebarSlice';
import { SIDEBAR_STORAGE } from '../constants/sidebar.constants';
import type { ISidebarState, IUseSidebar } from '../types/sidebar.types';

/**
 * Custom hook for managing sidebar state and behavior
 * @returns Sidebar state and control functions
 */
export const useSidebar = (): IUseSidebar => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { value: isExpanded } = useAppSelector((state) => state.sideBarData);
  
  // Local state for sidebar management
  const [state, setState] = useState<ISidebarState>({
    isExpanded: isExpanded,
    activePath: location.pathname,
    isVisible: true,
  });

  // Load saved state from localStorage on mount
  useEffect(() => {
    const savedExpansionState = localStorage.getItem(SIDEBAR_STORAGE.EXPANSION_STATE);
    const savedActiveRoute = localStorage.getItem(SIDEBAR_STORAGE.ACTIVE_ROUTE);
    
    if (savedExpansionState !== null) {
      setState(prev => ({
        ...prev,
        isExpanded: JSON.parse(savedExpansionState),
      }));
    }
    
    if (savedActiveRoute) {
      setState(prev => ({
        ...prev,
        activePath: savedActiveRoute,
      }));
    }
  }, []);

  // Update state when Redux state changes
  useEffect(() => {
    setState(prev => ({
      ...prev,
      isExpanded,
    }));
  }, [isExpanded]);

  // Update active path when location changes
  useEffect(() => {
    setState(prev => ({
      ...prev,
      activePath: location.pathname,
    }));
    
    // Save active route to localStorage
    localStorage.setItem(SIDEBAR_STORAGE.ACTIVE_ROUTE, location.pathname);
  }, [location.pathname]);

  /**
   * Toggle sidebar expansion state
   */
  const toggleExpansion = useCallback(() => {
    const newExpandedState = !isExpanded;
    
    // Dispatch Redux action
    if (newExpandedState) {
      dispatch(openSideBarFunc());
    } else {
      dispatch(closeSideBarFunc());
    }
    
    // Save to localStorage
    localStorage.setItem(SIDEBAR_STORAGE.EXPANSION_STATE, JSON.stringify(newExpandedState));
    
    setState(prev => ({
      ...prev,
      isExpanded: newExpandedState,
    }));
  }, [isExpanded, dispatch]);

  /**
   * Set active route
   * @param path - Route path to set as active
   */
  const setActiveRoute = useCallback((path: string) => {
    setState(prev => ({
      ...prev,
      activePath: path,
    }));
    
    // Save to localStorage
    localStorage.setItem(SIDEBAR_STORAGE.ACTIVE_ROUTE, path);
  }, []);

  /**
   * Check if a route is currently active
   * @param path - Route path to check
   * @returns True if route is active
   */
  const isRouteActive = useCallback((path: string): boolean => {
    if (!path) return false;
    
    // Exact match for root paths
    if (path === '/') {
      return state.activePath === '/';
    }
    
    // Check if active path includes the given path
    return state.activePath.includes(path);
  }, [state.activePath]);

  return {
    state,
    toggleExpansion,
    setActiveRoute,
    isRouteActive,
  };
};
